// frontend/src/utils/dbaRequestGenerator.js
import { DATIAFLOW_IP } from './firewallInstructions';

// ─── ENGINE_PORTS y ENGINE_LABELS eliminados ─────────────────────────────────
// Los puertos y labels vienen de sec_engine (catálogo BD) via props:
//   engineLabel  → t('wizard.engines.${engineType}') o selectedEngineData.label
//   port         → watch('port') que ya tiene el default_port del catálogo

// ─── pg_hba.conf instruction per method ──────────────────────────────────────
// NOTA: t se pasa como parámetro en cada función para i18n
const PG_HBA_INSTRUCTIONS = {
  ssh: (bastionIp, t) =>
    `   host  {database}  {user}  ${bastionIp || '{bastion_internal_ip}'}/32  md5\n` +
    `   ${t?.('dba_request.then_run') || 'Luego ejecutar'}: sudo systemctl reload postgresql`,
  firewall: (_, t) =>
    `   host  {database}  {user}  ${DATIAFLOW_IP}/32  md5\n` +
    `   ${t?.('dba_request.then_run') || 'Luego ejecutar'}: sudo systemctl reload postgresql`,
  direct: (_, t) =>
    `   host  {database}  {user}  ${DATIAFLOW_IP}/32  md5\n` +
    `   ${t?.('dba_request.then_run') || 'Luego ejecutar'}: sudo systemctl reload postgresql`,
};

// ─── Engine-specific auth instructions (non-PG) ─────────────────────────────
const AUTH_INSTRUCTIONS = {
  mysql: {
    ssh: (bastionIp) =>
      `   GRANT SELECT ON \`{database}\`.* TO '{user}'@'${bastionIp || '{bastion_internal_ip}'}' IDENTIFIED BY '***';\n` +
      `   FLUSH PRIVILEGES;`,
    firewall: () =>
      `   GRANT SELECT ON \`{database}\`.* TO '{user}'@'${DATIAFLOW_IP}' IDENTIFIED BY '***';\n` +
      `   FLUSH PRIVILEGES;`,
    direct: () =>
      `   GRANT SELECT ON \`{database}\`.* TO '{user}'@'${DATIAFLOW_IP}' IDENTIFIED BY '***';\n` +
      `   FLUSH PRIVILEGES;`,
  },
  mariadb: {
    ssh: (bastionIp) =>
      `   GRANT SELECT ON \`{database}\`.* TO '{user}'@'${bastionIp || '{bastion_internal_ip}'}' IDENTIFIED BY '***';\n` +
      `   FLUSH PRIVILEGES;`,
    firewall: () =>
      `   GRANT SELECT ON \`{database}\`.* TO '{user}'@'${DATIAFLOW_IP}' IDENTIFIED BY '***';\n` +
      `   FLUSH PRIVILEGES;`,
    direct: () =>
      `   GRANT SELECT ON \`{database}\`.* TO '{user}'@'${DATIAFLOW_IP}' IDENTIFIED BY '***';\n` +
      `   FLUSH PRIVILEGES;`,
  },
  mssql: {
    _common: (t) =>
      `   1. ${t?.('dba_request.mssql_step1') || 'Verificar que el Login esté habilitado en SQL Server Management Studio'}\n` +
      `   2. ${t?.('dba_request.mssql_step2') || 'Verificar que "SQL Server and Windows Authentication mode" esté activo'}\n` +
      `   3. ${t?.('dba_request.mssql_step3') || 'Asegurar que el usuario tiene permisos sobre la base de datos'}`,
  },
  oracle: {
    _common: (t) =>
      `   1. ${t?.('dba_request.oracle_step1') || 'Verificar que el listener esté activo'}: lsnrctl status\n` +
      `   2. ${t?.('dba_request.oracle_step2') || 'Verificar tnsnames.ora y que el servicio esté registrado'}\n` +
      `   3. GRANT CONNECT, SELECT ANY TABLE TO {user};`,
  },
};

/**
 * Generates a context-aware DBA access request text.
 *
 * @param {Object}  params
 * @param {string}  params.engineType         - 'postgres', 'mysql', etc.
 * @param {string}  params.host               - DB host
 * @param {string}  params.port               - DB port
 * @param {string}  params.database           - DB name
 * @param {string}  params.user               - DB user
 * @param {string}  params.connectionMethod   - 'ssh' | 'firewall' | 'direct'
 * @param {string}  params.networkType        - 'cloud' | 'local'
 * @param {string}  params.sshHost            - SSH bastion host
 * @param {string}  params.sshPort            - SSH bastion port
 * @param {string}  params.sshUser            - SSH user
 * @param {boolean} params.isTarget           - Whether this is the target side
 * @param {Function} params.t                 - i18n translate function
 * @returns {string} Formatted text ready to copy/paste
 */
export function generateDbaRequest({
  engineType, engineLabel, host, port, database, user,
  connectionMethod, networkType,
  sshHost, sshPort, sshUser,
  isTarget = false,
  t,
}) {
  const label = engineLabel || engineType?.toUpperCase() || 'Base de datos';
  const actualPort = port || '???';
  const side = isTarget
    ? (t?.('dba_request.side_target') || 'destino (lectura + escritura)')
    : (t?.('dba_request.side_source') || 'origen (solo lectura)');
  const permsLabel = isTarget
    ? 'SELECT, INSERT, UPDATE, DELETE, CREATE'
    : `SELECT (${t?.('dba_request.read_only') || 'solo lectura'})`;

  const lines = [];

  // ── HEADER ──────────────────────────────────────────────────────────────────
  lines.push('═══════════════════════════════════════════════════════════');
  lines.push(t?.('dba_request.title') || 'SOLICITUD DE ACCESO A BASE DE DATOS');
  lines.push(t?.('dba_request.subtitle') || 'Para migración/sincronización de datos via DatiaFlow');
  lines.push('═══════════════════════════════════════════════════════════');
  lines.push('');

  // ── CONNECTION INFO ─────────────────────────────────────────────────────────
  lines.push(`▸ ${t?.('dba_request.engine') || 'Motor'}:     ${label}`);
  lines.push(`▸ ${t?.('dba_request.db') || 'Base de datos'}: ${database || '{nombre_base_datos}'}`);
  lines.push(`▸ ${t?.('dba_request.user_label') || 'Usuario'}:  ${user || '{usuario}'}`);
  lines.push(`▸ ${t?.('dba_request.port_label') || 'Puerto'}:   ${actualPort}`);
  lines.push(`▸ ${t?.('dba_request.purpose') || 'Propósito'}: ${side}`);
  lines.push(`▸ ${t?.('dba_request.perms') || 'Permisos mínimos'}: ${permsLabel}`);
  lines.push('');

  // ── ACTIONS REQUIRED ────────────────────────────────────────────────────────
  lines.push('───────────────────────────────────────────────────────────');
  lines.push(t?.('dba_request.actions_title') || 'ACCIONES REQUERIDAS POR EL DBA:');
  lines.push('───────────────────────────────────────────────────────────');
  lines.push('');

  const method = networkType === 'cloud' ? 'firewall' : (connectionMethod || 'firewall');
  let stepNum = 1;

  // Step: Firewall / SSH access
  if (method === 'ssh') {
    lines.push(`${stepNum}. ${t?.('dba_request.ssh_whitelist') || 'Autorizar la IP de DatiaFlow para conexión SSH al bastion'}:`);
    lines.push(`   IP: ${DATIAFLOW_IP}`);
    lines.push(`   Bastion: ${sshHost || '{ssh_host}'}:${sshPort || '22'}`);
    lines.push(`   ${t?.('dba_request.ssh_user_label') || 'Usuario SSH'}: ${sshUser || '{ssh_user}'}`);
    lines.push('');
    stepNum++;
  } else if (method === 'firewall') {
    lines.push(`${stepNum}. ${t?.('dba_request.firewall_whitelist') || 'Agregar IP de DatiaFlow al firewall'}:`);
    lines.push(`   IP: ${DATIAFLOW_IP}/32`);
    lines.push(`   ${t?.('dba_request.protocol') || 'Protocolo'}: TCP`);
    lines.push(`   ${t?.('dba_request.port_label') || 'Puerto'}: ${actualPort}`);
    lines.push(`   ${t?.('dba_request.direction') || 'Dirección'}: ${t?.('dba_request.inbound') || 'Entrada (Inbound)'}`);
    lines.push('');
    stepNum++;
  }

  // Step: Engine-specific auth (pg_hba, MySQL GRANT, etc.)
  if (engineType === 'postgres' || engineType === 'redshift') {
    lines.push(`${stepNum}. ${t?.('dba_request.pg_hba_step') || 'Agregar entrada en pg_hba.conf del servidor PostgreSQL'}:`);
    const pgFn = PG_HBA_INSTRUCTIONS[method] || PG_HBA_INSTRUCTIONS.firewall;
    let pgText = pgFn(sshHost, t);
    pgText = pgText.replace(/\{database\}/g, database || '{base_datos}');
    pgText = pgText.replace(/\{user\}/g, user || '{usuario}');
    lines.push(pgText);
    lines.push('');
    stepNum++;
  } else if (['mysql', 'mariadb'].includes(engineType)) {
    const mysqlInst = AUTH_INSTRUCTIONS[engineType];
    if (mysqlInst) {
      lines.push(`${stepNum}. ${t?.('dba_request.mysql_grant_step') || 'Verificar que el usuario tenga permisos desde la IP de conexión'}:`);
      const fn = mysqlInst[method] || mysqlInst.firewall;
      let grantText = fn ? fn(sshHost) : (mysqlInst._common?.() || '');
      grantText = grantText.replace(/\{database\}/g, database || '{base_datos}');
      grantText = grantText.replace(/\{user\}/g, user || '{usuario}');
      lines.push(grantText);
      lines.push('');
      stepNum++;
    }
  } else if (['mssql', 'azure_sql', 'oracle'].includes(engineType)) {
    const inst = AUTH_INSTRUCTIONS[engineType] || AUTH_INSTRUCTIONS[engineType === 'azure_sql' ? 'mssql' : engineType];
    if (inst?._common) {
      lines.push(`${stepNum}. ${t?.('dba_request.engine_auth_step') || 'Verificar configuración de autenticación'}:`);
      let authText = inst._common(t);
      authText = authText.replace(/\{user\}/g, user || '{usuario}');
      lines.push(authText);
      lines.push('');
      stepNum++;
    }
  }

  // Step: Test verification
  lines.push(`${stepNum}. ${t?.('dba_request.verify_step') || 'Verificar la conectividad'}:`);
  lines.push(`   ${t?.('dba_request.verify_desc') || 'Una vez aplicados los cambios, confirmar que la conexión funciona desde DatiaFlow.'}`);
  lines.push('');

  // ── FOOTER ──────────────────────────────────────────────────────────────────
  lines.push('───────────────────────────────────────────────────────────');
  lines.push(`${t?.('dba_request.generated_by') || 'Generado por DatiaFlow'} — ${new Date().toLocaleDateString()}`);
  lines.push(`${t?.('dba_request.support_contact') || 'Soporte'}: support@varanext.com`);
  lines.push('═══════════════════════════════════════════════════════════');

  return lines.join('\n');
}

/**
 * Generates a short diagnostic-specific DBA remediation text.
 * Used when test-connection fails with a known DB auth error.
 *
 * @param {Object}  params
 * @param {string}  params.diagnosticCode   - e.g. 'PG_HBA_REJECTED', 'MYSQL_HOST_BLOCKED'
 * @param {string}  params.engineType
 * @param {string}  params.host
 * @param {string}  params.port
 * @param {string}  params.database
 * @param {string}  params.user
 * @param {string}  params.connectionMethod
 * @param {string}  params.sshHost
 * @param {Function} params.t
 * @returns {string} Short actionable remediation text
 */
export function generateDiagnosticRemediation({
  diagnosticCode, engineType, host, port, database, user,
  connectionMethod, sshHost, t,
}) {
  const connectingIp = connectionMethod === 'ssh'
    ? (sshHost || '{bastion_ip}')
    : DATIAFLOW_IP;

  const templates = {
    PG_HBA_REJECTED: () => {
      const lines = [
        t?.('dba_request.diag_pg_hba') || 'PostgreSQL rechazó la conexión porque la IP no está autorizada en pg_hba.conf.',
        '',
        t?.('dba_request.diag_ask_dba') || 'Pide a tu DBA que agregue esta línea:',
        `   host  ${database || '{db}'}  ${user || '{user}'}  ${connectingIp}/32  md5`,
        '',
        `   sudo systemctl reload postgresql`,
      ];
      return lines.join('\n');
    },
    MYSQL_HOST_BLOCKED: () => {
      const lines = [
        t?.('dba_request.diag_mysql_host') || 'MySQL rechazó la conexión porque el usuario no tiene permiso desde esta IP.',
        '',
        t?.('dba_request.diag_ask_dba') || 'Pide a tu DBA que ejecute:',
        `   GRANT ALL ON \`${database || '{db}'}\`.* TO '${user || '{user}'}'@'${connectingIp}' IDENTIFIED BY '***';`,
        `   FLUSH PRIVILEGES;`,
      ];
      return lines.join('\n');
    },
    ORACLE_LISTENER_DOWN: () => {
      return (t?.('dba_request.diag_oracle_listener') || 'El listener de Oracle no respondió.') + '\n\n' +
        (t?.('dba_request.diag_ask_dba') || 'Pide a tu DBA que verifique:') + '\n' +
        '   lsnrctl status\n   lsnrctl start';
    },
    ORACLE_AUTH_FAILED: () => {
      return (t?.('dba_request.diag_oracle_auth') || 'Oracle rechazó las credenciales.') + '\n\n' +
        (t?.('dba_request.diag_ask_dba') || 'Pide a tu DBA que verifique:') + '\n' +
        `   ALTER USER ${user || '{user}'} IDENTIFIED BY "***";\n` +
        `   GRANT CONNECT, SELECT ANY TABLE TO ${user || '{user}'};`;
    },
    MSSQL_LOGIN_FAILED: () => {
      return (t?.('dba_request.diag_mssql_login') || 'SQL Server rechazó las credenciales.') + '\n\n' +
        (t?.('dba_request.diag_ask_dba') || 'Pide a tu DBA que verifique:') + '\n' +
        `   1. ${t?.('dba_request.mssql_step2') || 'Verificar que "SQL Server and Windows Authentication mode" esté activo'}\n` +
        `   2. ${t?.('dba_request.diag_mssql_login_enabled', { user: user || '{user}' }) || `Que el Login '${user || '{user}'}' esté habilitado`}\n` +
        `   3. ${t?.('dba_request.diag_mssql_db_access', { db: database || '{db}' }) || `Que el usuario tenga acceso a la base '${database || '{db}'}'`}`;
    },
  };

  const fn = templates[diagnosticCode];
  return fn ? fn() : '';
}
